import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';
import { getPersonalExpenses, addPersonalExpense, deletePersonalExpense } from '../../services/clientService';
import styles from './PersonalExpenses.module.css';

const PersonalExpenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ description: '', amount: '', category: 'Food & Dining', date: new Date().toISOString().split('T')[0] });

  const [filterType, setFilterType] = useState('month');
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    setIsLoading(true);
    const data = await getPersonalExpenses();
    setExpenses(data);
    setIsLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description || !formData.amount) return;
    
    setIsLoading(true);
    try {
      const newExp = await addPersonalExpense({
        ...formData,
        amount: parseFloat(formData.amount),
        date: new Date(formData.date).toISOString()
      });
      setExpenses([newExp, ...expenses].sort((a,b) => new Date(b.date) - new Date(a.date)));
      setShowForm(false);
      setFormData({ description: '', amount: '', category: 'Food & Dining', date: new Date().toISOString().split('T')[0] });
    } catch (error) {
      alert("Failed to add personal expense");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this expense?")) {
      await deletePersonalExpense(id);
      setExpenses(expenses.filter(e => e.id !== id));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const totalSpent = expenses.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  
  const getFilterRange = () => {
    const d = new Date(filterDate);
    if (isNaN(d.getTime())) return { start: 0, end: Infinity };
    
    if (filterType === 'day') {
      const start = new Date(d);
      start.setHours(0,0,0,0);
      const end = new Date(d);
      end.setHours(23,59,59,999);
      return { start: start.getTime(), end: end.getTime() };
    } else if (filterType === 'week') {
      const start = new Date(d);
      start.setHours(0,0,0,0);
      const day = start.getDay();
      const diff = start.getDate() - day + (day === 0 ? -6 : 1); 
      start.setDate(diff);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23,59,59,999);
      return { start: start.getTime(), end: end.getTime() };
    } else if (filterType === 'month') {
      const start = new Date(d.getFullYear(), d.getMonth(), 1);
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
      return { start: start.getTime(), end: end.getTime() };
    }
    return { start: 0, end: Infinity };
  };

  const range = getFilterRange();
  const filteredExpenses = expenses.filter(item => {
    const time = new Date(item.date).getTime();
    return time >= range.start && time <= range.end;
  });

  const timeframeSpent = filteredExpenses.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  const categoryMap = {};
  filteredExpenses.forEach(exp => {
    const cat = exp.category || 'Other';
    if (!categoryMap[cat]) categoryMap[cat] = 0;
    categoryMap[cat] += parseFloat(exp.amount) || 0;
  });
  
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#64748b'];
  const chartData = Object.keys(categoryMap).map((cat, index) => ({
    name: cat,
    value: categoryMap[cat],
    color: COLORS[index % COLORS.length]
  }));

  return (
    <div className={styles.expensesContainer} style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ marginBottom: '20px' }}>
        <Link to="/crm" style={{ display: 'inline-block', backgroundColor: '#1e293b', color: '#cbd5e1', padding: '8px 16px', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold' }}>
          &larr; Back to CRM
        </Link>
      </div>
      <div className={styles.header}>
        <div>
          <h1 className={styles.pageTitle} style={{ color: 'white' }}>Personal Expenses</h1>
          <p className={styles.pageSubtitle} style={{ color: '#94a3b8' }}>Track your private spending independently.</p>
        </div>
        <button className={styles.addBtn} onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Record Expense'}
        </button>
      </div>

      <div className={styles.statsCards}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>📉</div>
          <div className={styles.statInfo}>
            <h3>{filterType === 'day' ? "Today's" : filterType === 'week' ? "This Week's" : "This Month's"} Spending</h3>
            <p>{formatCurrency(timeframeSpent)}</p>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>💸</div>
          <div className={styles.statInfo}>
            <h3>Total Lifetime Logged</h3>
            <p>{formatCurrency(totalSpent)}</p>
          </div>
        </div>
      </div>

      <div className={styles.filterBar}>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select 
            value={filterType} 
            onChange={e => setFilterType(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="day">Day-wise</option>
            <option value="week">Week-wise</option>
            <option value="month">Month-wise</option>
          </select>
          <input 
            type="date" 
            value={filterDate} 
            onChange={e => setFilterDate(e.target.value)} 
            className={styles.filterSelect}
          />
        </div>
      </div>

      {!isLoading && filteredExpenses.length > 0 && (
        <div className={styles.chartWrapper}>
          <h3 className={styles.chartTitle}>Category Breakdown</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
                stroke="none"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip 
                formatter={(value) => formatCurrency(value)}
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: 'white' }}
                itemStyle={{ color: 'white' }}
              />
              <Legend verticalAlign="bottom" height={36} wrapperStyle={{ paddingTop: '20px', color: '#cbd5e1' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {showForm && (
        <div className={styles.formWrapper}>
          <form onSubmit={handleSubmit}>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label>What did you spend on?</label>
                <input 
                  type="text" 
                  value={formData.description} 
                  onChange={e => setFormData({...formData, description: e.target.value})} 
                  placeholder="e.g., Groceries, Rent, Entertainment"
                  required 
                />
              </div>
              <div className={styles.formGroup}>
                <label>Amount (₹)</label>
                <input 
                  type="number" 
                  value={formData.amount} 
                  onChange={e => setFormData({...formData, amount: e.target.value})} 
                  placeholder="Amount"
                  required 
                />
              </div>
              <div className={styles.formGroup}>
                <label>Date</label>
                <input 
                  type="date" 
                  value={formData.date} 
                  onChange={e => setFormData({...formData, date: e.target.value})} 
                  required 
                />
              </div>
              <div className={styles.formGroup}>
                <label>Category</label>
                <select 
                  value={formData.category} 
                  onChange={e => setFormData({...formData, category: e.target.value})}
                >
                  <option value="Food & Dining">Food & Dining</option>
                  <option value="Alcohol">Alcohol</option>
                  <option value="Transport">Transport</option>
                  <option value="Shopping">Shopping</option>
                  <option value="Bills & Utilities">Bills & Utilities</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            <div className={styles.formActions}>
              <button type="submit" className={styles.submitBtn}>Save Expense</button>
              <button type="button" className={styles.cancelBtn} onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Loading expenses...</div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.expensesTable}>
            <thead>
              <tr>
                <th>Item / Description</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>No expenses found for this timeframe.</td>
                </tr>
              ) : (
                filteredExpenses.map(expense => (
                  <tr key={expense.id}>
                    <td>
                      <div className={styles.expenseTitle}>{expense.description}</div>
                      <div className={styles.expenseCategory}>{expense.category}</div>
                    </td>
                    <td style={{ color: '#cbd5e1' }}>
                      {new Date(expense.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className={styles.amount}>
                      -{formatCurrency(parseFloat(expense.amount) || 0)}
                    </td>
                    <td>
                      <button className={styles.deleteBtn} onClick={() => handleDelete(expense.id)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PersonalExpenses;
